<template>
    <div v-if="show" class="share-popup-overlay" @click.self="close">
        <div class="share-popup-box">
            <!-- Title and close -->
            <div class="title-bar d-flex justify-content-center align-items-center">
                <h5 class="title">Share product</h5>
                <button class="btn-close" @click="close"></button>
            </div>

            <!-- Header -->
            <div class="header-card">
                <template v-if="file">
                    <img :src="imageSrc" alt="Product Image" class="profile-img" />
                </template>
                <template v-else>
                    <!-- Match SharePopup default avatar style for consistency -->
                    <svg
                        class="profile-img"
                        xmlns="http://www.w3.org/2000/svg"
                        width="200"
                        height="200"
                        viewBox="0 0 100 100"
                        role="img"
                        aria-label="Default profile avatar"
                    >
                        <defs>
                            <linearGradient id="avatarGradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stop-color="#A5B4FC" />
                                <stop offset="100%" stop-color="#FBCFE8" />
                            </linearGradient>
                        </defs>
                        <circle cx="50" cy="50" r="48" fill="url(#avatarGradient)" />
                        <circle cx="50" cy="38" r="16" fill="#FFFFFF" opacity="0.95" />
                        <path d="M20 74c0-14 15-20 30-20s30 6 30 20v4H20v-4z" fill="#FFFFFF" opacity="0.95" />
                    </svg>
                </template>
                <h3 class="username">{{ title }}</h3>
                <small class="handle">#{{ productId }}</small>
            </div>

            <!-- Social icons -->
            <div class="horizontal-share-container">
                <div class="share-scroll-row">
                    <div class="share-item" @click="shareTo('snapchat')">
                        <div class="icon-bg snapchat-bg">
                            <i class="fab fa-snapchat-ghost"></i>
                        </div>
                        <small>Snapchat</small>
                    </div>

                    <div class="share-item" @click="shareTo('facebook')">
                        <div class="icon-bg facebook-bg">
                            <i class="fab fa-facebook-f"></i>
                        </div>
                        <small>Facebook</small>
                    </div>

                    <div class="share-item" @click="shareTo('linkedin')">
                        <div class="icon-bg linkedin-bg">
                            <i class="fab fa-linkedin-in"></i>
                        </div>
                        <small>LinkedIn</small>
                    </div>

                    <div class="share-item" @click="shareTo('twitter')">
                        <div class="icon-bg twitter-bg">
                            <svg
                                width="192"
                                height="192"
                                viewBox="0 0 192 192"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                focusable="false"
                                aria-hidden="true"
                                style="pointer-events: none; display: inherit; width: 100%; height: 100%;"
                            >
                                <rect width="192" height="192" rx="96" fill="black"></rect>
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M42 47H76L100 78.5L127 47H144L107.5 88.5L150 145H117L91 111L61 145H44L83 100.5L42 47ZM62 57H71.5L130.5 135H121.5L62 57Z"
                                    fill="white"
                                ></path>
                            </svg>
                        </div>
                        <small>X</small>
                    </div>

                    <div class="share-item" @click="shareTo('whatsapp')">
                        <div class="icon-bg whatsapp-bg">
                            <i class="fab fa-whatsapp"></i>
                        </div>
                        <small>WhatsApp</small>
                    </div>

                    <div class="share-item" @click="shareTo('messenger')">
                        <div class="icon-bg messenger-bg">
                            <i class="fab fa-facebook-messenger"></i>
                        </div>
                        <small>Messenger</small>
                    </div>

                    <div class="share-item" @click="shareTo('email')">
                        <div class="icon-bg email-bg">
                            <i class="fas fa-envelope"></i>
                        </div>
                        <small>Email</small>
                    </div>
                </div>
            </div>

            <!-- Copy link -->
            <div class="mt-3 d-flex align-items-center copy-link-box">
                <input type="text" :value="shareUrl" readonly class="form-control me-2" />

                <AppButton
                    :black="true"
                    :disabled="copyText === 'Copied!'"
                    :ignorePlanRestriction="true"
                    @buttonClick="copyToClipboard"
                >
                    {{ copyText }}
                </AppButton>
            </div>

            <!-- CTA -->
            <div class="cta-section mt-4">
                <p class="cta-title">Join {{ username }} on Allmyonline</p>
                <p class="cta-subtitle">
                    Bring all your content together with our free, customizable
                    bio page for seamless sharing.
                </p>
                <div class="cta-buttons">
                    <AppButton
                        :black="true"
                        width="100%"
                        :href="signupUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop="openLink(signupUrl)"
                        :ignore-plan-restriction="true"
                    >
                        Sign up free
                    </AppButton>
                    <AppButton
                        :secondary="true"
                        width="100%"
                        :href="infoUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop="openLink(infoUrl)"
                        :ignore-plan-restriction="true"
                    >
                        Find out more
                    </AppButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppButton from "@/Components/AppButton.vue";

export default {
    name: "ShareProductPopup",
    components: { AppButton },
    props: {
        show: { type: Boolean, default: false },
        url: { type: String, required: true },
        productId: { type: [String, Number], required: true },
        title: { type: String, default: "" },
        file: { type: String, default: null },
        DoEndpoint: { type: String, required: true },
        DoBucket: { type: String, required: true },
        // To align with SharePopup CTA section
        username: { type: String, required: true },
        urlName: { type: String, required: true },
        referral_code: { type: String, default: null },
    },
    emits: ["close"],
    data() {
        return {
            copyText: "Copy",
        };
    },
    computed: {
        shareUrl() {
            return this.url || window.location.href;
        },
        imageSrc() {
            if (!this.file) return null;
            if (this.file.startsWith("demo_")) {
                return `${window.location.origin}/storage/${this.file}`;
            }
            return `${this.DoEndpoint}/${this.DoBucket}/products/${this.file}`;
        },
        signupUrl() {
            const usernameParam = encodeURIComponent(this.username || "");
            const refParam = this.referral_code
                ? `&ref=${encodeURIComponent(this.referral_code)}`
                : "";
            return `${this.urlName}signup/details?username=${usernameParam}${refParam}`;
        },
        infoUrl() {
            return this.referral_code ? `${this.urlName}?ref=${encodeURIComponent(this.referral_code)}` : this.urlName;
        },
    },
    methods: {
        copyToClipboard() {
            navigator.clipboard.writeText(this.shareUrl).then(() => {
                this.copyText = "Copied!";
                setTimeout(() => (this.copyText = "Copy"), 3000);
            });
        },
        shareTo(platform) {
            const encoded = encodeURIComponent(this.shareUrl);
            const urls = {
                snapchat: `https://www.snapchat.com/share?url=${encoded}`,
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
                linkedin: `https://www.linkedin.com/shareArticle?url=${encoded}`,
                twitter: `https://twitter.com/intent/tweet?url=${encoded}`,
                whatsapp: `https://wa.me/?text=${encoded}`,
                messenger: `https://www.messenger.com/new/?link=${encoded}`,
                email: `mailto:?subject=${encodeURIComponent('Check this product')}&body=${encoded}`,
            };
            window.open(urls[platform], "_blank");
        },
        openLink(url) {
            window.open(url, "_blank");
        },
        close() {
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
.share-popup-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
}

.share-popup-box {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    width: 520px;
    max-width: 95%;
}

/* Match SharePopup header styles */
.header-card {
    background-color: #222;
    border-radius: 16px 16px 0 0;
    text-align: center;
    padding: 40px 24px 60px;
}
.card-title {
    margin: 0 0 16px;
    color: #fff;
    font-size: 1.125rem;
}
.header-card .profile-img {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    object-fit: cover;
    margin: 0 auto 16px;
}
.header-card .username {
    margin: 0;
    color: #fff;
    font-size: 1.5rem;
    font-weight: bold;
}
.header-card .handle {
    display: block;
    margin-top: 8px;
    color: #ccc;
    font-size: 1rem;
}

/* Horizontal share list */
.horizontal-share-container { overflow-x: auto; padding: 10px 0; }
.share-scroll-row { display: flex; flex-wrap: nowrap; gap: 16px; }
.share-item { min-width: 60px; text-align: center; cursor: pointer; flex-shrink: 0; }
.icon-bg { width: 48px; height: 48px; border-radius: 50%; margin: 0 auto 6px; display: flex; justify-content: center; align-items: center; font-size: 18px; color: white; }

/* Platform-specific backgrounds */
.snapchat-bg { background-color: #fffc00; color: black; }
.facebook-bg { background-color: #3b5998; }
.linkedin-bg { background-color: #0077b5; }
.twitter-bg { background-color: #000000; }
.whatsapp-bg { background-color: #25d366; }
.messenger-bg { background-color: #0084ff; }
.email-bg { background-color: #db4437; }

/* Copy field */
.copy-link-box input { flex: 1; }

.title-bar { position: relative; }
.title-bar .btn-close { position: absolute; right: 0; top: 50%; transform: translateY(-50%); }

/* CTA styles to match SharePopup */
.cta-section { padding: 16px; border-top: 1px solid #eee; }
.cta-title { margin: 0 0 4px; font-weight: bold; font-size: 1rem; }
.cta-subtitle { margin: 0 0 12px; color: #666; font-size: 0.875rem; }
.cta-buttons { display: flex; gap: 8px; }

/* Responsive behavior aligned with SharePopup */
@media (max-width: 576px) {
    .share-popup-overlay { align-items: stretch; }
    .share-popup-box {
        width: 100%;
        max-width: none;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
        padding: 16px;
        margin: 0;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }
    .header-card { border-radius: 0; }
    .cta-buttons { flex-direction: column; }
}
</style>
